import { useState, useEffect } from "react";
import { Calendar, CheckCircle, XCircle, Loader2, LogOut } from "lucide-react";
import axios from "axios";
import SeoHead from "@/components/seo/SeoHead";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPage = () => {
  const [googleStatus, setGoogleStatus] = useState({ connected: false, email: null });
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);

  useEffect(() => {
    checkGoogleStatus();
    
    // Check URL params for auth result
    const params = new URLSearchParams(window.location.search);
    const authResult = params.get('google_auth');
    if (authResult === 'success') {
      checkGoogleStatus();
      window.history.replaceState({}, '', '/admin');
    }
  }, []);

  const checkGoogleStatus = async () => {
    try {
      const response = await axios.get(`${API}/google/status`);
      setGoogleStatus(response.data);
    } catch (error) {
      console.error("Error checking Google status:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const connectGoogle = async () => {
    setIsConnecting(true);
    try {
      const response = await axios.get(`${API}/google/login`);
      window.location.href = response.data.authorization_url;
    } catch (error) {
      console.error("Error connecting Google:", error);
      setIsConnecting(false);
    }
  };

  const disconnectGoogle = async () => {
    try {
      await axios.post(`${API}/google/disconnect`);
      setGoogleStatus({ connected: false, email: null });
    } catch (error) {
      console.error("Error disconnecting Google:", error);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 py-12 px-4">
      <SeoHead title="Admin | Pellon Morel Solutions" description="Administration panel." path="/admin" noindex />
      <div className="max-w-lg mx-auto">
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-4">
              <Calendar className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-[#0F172A]" style={{ fontFamily: "'Playfair Display', serif" }}>
              Google Calendar
            </h1>
            <p className="text-slate-500 text-sm mt-2">
              Conecta tu calendario para recibir las citas automáticamente
            </p>
          </div>

          {isLoading ? (
            <div className="text-center py-8">
              <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin mx-auto" />
              <p className="text-slate-500 mt-2">Verificando conexión...</p>
            </div>
          ) : googleStatus.connected ? (
            <div className="text-center">
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-6">
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                <p className="text-green-700 font-semibold">¡Calendario Conectado!</p>
                <p className="text-green-600 text-sm mt-1">{googleStatus.email}</p>
              </div>
              <p className="text-slate-600 text-sm mb-6">
                Las nuevas citas se guardarán automáticamente en tu Google Calendar.
              </p>
              <button
                onClick={disconnectGoogle}
                className="flex items-center justify-center gap-2 w-full bg-slate-100 text-slate-600 px-6 py-3 rounded-lg hover:bg-slate-200 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                Desconectar Calendario
              </button>
            </div>
          ) : (
            <div className="text-center">
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 mb-6">
                <XCircle className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                <p className="text-slate-600 font-semibold">Calendario No Conectado</p>
                <p className="text-slate-500 text-sm mt-1">
                  Conecta tu cuenta de Google para sincronizar citas
                </p>
              </div>
              <button
                onClick={connectGoogle}
                disabled={isConnecting}
                className="flex items-center justify-center gap-3 w-full bg-white border-2 border-slate-200 text-slate-700 px-6 py-4 rounded-lg hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all disabled:opacity-50"
              >
                {isConnecting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                )}
                {isConnecting ? "Conectando..." : "Conectar con Google"}
              </button>
            </div>
          )}

          <div className="mt-8 pt-6 border-t border-slate-100">
            <a 
              href="/" 
              className="block text-center text-sm text-slate-500 hover:text-[#D4AF37] transition-colors"
            >
              ← Volver al sitio principal
            </a>
          </div>
        </div>

        <p className="text-center text-slate-400 text-xs mt-6">
          Pellon-Morel Solutions LLC © {new Date().getFullYear()}
        </p>
      </div>
    </div>
  );
};

export default AdminPage;
